/**
 * Manpower P&L — Attendance API service.
 *
 * Types from the OpenAPI-generated client (src/api/generated/manpower-attendance.ts).
 * Day-to-day flow (the ONLY user entry point): POST /attendance/roll with
 * { facilityId, date, shiftType: Day|Night } → the shift is auto-created if needed and the
 * pre-filled roster comes back → bulk-mark present/absent + OT → freeze (runs the cost
 * engine). The roster is AUTO-BUILT from each worker's facility assignment — no manual
 * worker→shift mapping, and no user-facing shift id.
 *
 * The shift-id endpoints (listShifts / getRoll) are INTERNAL/ADMIN-ONLY now — kept for
 * admin tooling, not the daily UI.
 */
import { apiService } from './api';
import type { components } from '../api/generated/manpower-attendance';

export type FacilitySubmissionStatus = components['schemas']['FacilitySubmissionStatus'];
export type ShiftSummary = components['schemas']['ShiftSummary'];
export type AttendanceRoll = components['schemas']['AttendanceRoll'];
export type AttendanceRow = components['schemas']['AttendanceRow'];
export type BulkMarkEntry = components['schemas']['BulkMarkEntry'];
export type FreezeResult = components['schemas']['FreezeResult'];
export type OpenRollBody = components['schemas']['OpenRollBody'];
export type OpenRollResult = components['schemas']['OpenRollResult'];

export class ManpowerAttendanceApiService {
	/**
	 * POST /attendance/roll — the day-to-day entry point. Opens (auto-creating if needed)
	 * the roll for facility + date + Day|Night and returns the internal shift_id + roster.
	 */
	static async openRoll(body: OpenRollBody): Promise<OpenRollResult> {
		const res = await apiService.post<{ data: OpenRollResult }>('/manpower/attendance/roll', body);
		return (res as unknown as { data: OpenRollResult }).data;
	}

	/** GET /attendance/status — per-facility submission status for a city + date. */
	static async getStatus(cityId: number, date: string): Promise<FacilitySubmissionStatus[]> {
		const res = await apiService.get<{ data: FacilitySubmissionStatus[] }>(
			`/manpower/attendance/status?cityId=${cityId}&date=${encodeURIComponent(date)}`
		);
		return (res as unknown as { data?: FacilitySubmissionStatus[] }).data ?? [];
	}

	/** INTERNAL/ADMIN — GET /attendance/shifts?facilityId=&date= (shift discovery, not in daily UI). */
	static async listShifts(facilityId: number, date: string): Promise<ShiftSummary[]> {
		const res = await apiService.get<{ data: ShiftSummary[] }>(
			`/manpower/attendance/shifts?facilityId=${facilityId}&date=${encodeURIComponent(date)}`
		);
		return (res as unknown as { data?: ShiftSummary[] }).data ?? [];
	}

	/** INTERNAL/ADMIN — GET /attendance/shift/{shiftId} (raw roll by id). */
	static async getRoll(shiftId: number): Promise<AttendanceRoll> {
		const res = await apiService.get<{ data: AttendanceRoll }>(
			`/manpower/attendance/shift/${shiftId}`
		);
		return (res as unknown as { data: AttendanceRoll }).data;
	}

	/** PUT /attendance/shift/{shiftId} — bulk-mark attendance (atomic upsert, last-write-wins). */
	static async bulkMark(shiftId: number, entries: BulkMarkEntry[]): Promise<void> {
		await apiService.put(`/manpower/attendance/shift/${shiftId}`, { entries });
	}

	/** POST /attendance/shift/{shiftId}/freeze — freeze the shift; runs the cost engine. */
	static async freeze(shiftId: number): Promise<FreezeResult> {
		const res = await apiService.post<{ data: FreezeResult }>(
			`/manpower/attendance/shift/${shiftId}/freeze`
		);
		return (res as unknown as { data: FreezeResult }).data;
	}
}
